import { useState, useCallback } from 'react';
import { apiService, AppointmentRequest } from '../services/api';
import type { AppointmentResponse } from '../services/api';
import type { OwnerAppointmentListParams, OwnerAppointmentListResponse } from '../types/api-responses';
import { getApiErrorMessage } from '../types/api-errors';

export const useAppointmentsOwner = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getAppointments = useCallback(async (params?: OwnerAppointmentListParams): Promise<OwnerAppointmentListResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getOwnerAppointments(params);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch appointments');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const createAppointment = useCallback(async (data: AppointmentRequest): Promise<AppointmentResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.createAppointment(data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to create appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getAppointmentById = useCallback(async (id: number): Promise<AppointmentResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getAppointmentById(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateAppointment = useCallback(async (id: number, data: Omit<AppointmentRequest, 'businessId'>): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.updateAppointment(id, data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to update appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteAppointment = useCallback(async (id: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteAppointment(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to delete appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const rescheduleAppointment = useCallback(async (id: number, newDate: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.rescheduleOwnerAppointment(id, newDate);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to reschedule appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        getAppointments,
        createAppointment,
        getAppointmentById,
        updateAppointment,
        deleteAppointment,
        rescheduleAppointment,
        loading,
        error
    };
};
